import React, { useEffect, useState, Component } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset } from 'firebase/auth';
import { auth } from '../../firebase';
import { AlertCircleIcon, CheckCircleIcon, EyeIcon, EyeOffIcon, LockIcon } from 'lucide-react';
// MODULE-LEVEL LOG: This proves the file is included in the build
console.log('[ResetPasswordPage] Module loaded — build marker: RPP_2025_03_21_A');
// Error boundary to catch any render crashes
class ResetPasswordErrorBoundary extends Component<{
  children: React.ReactNode;
}, {
  hasError: boolean;
  error: string;
}> {
  constructor(props: {
    children: React.ReactNode;
  }) {
    super(props);
    this.state = {
      hasError: false,
      error: ''
    };
  }
  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error: error.message
    };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ResetPasswordPage] Render crash:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(to right, #0F1626, #1E3A8A, #2C3E50)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }} data-id="element-6054">
          <div style={{
          maxWidth: '28rem',
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '0.75rem',
          padding: '2.5rem 2rem',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
        }} data-id="element-6055">
            <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#111827',
            marginBottom: '1rem'
          }} data-id="element-6056">
              Something went wrong
            </h2>
            <p style={{
            color: '#6B7280',
            marginBottom: '1rem'
          }} data-id="element-6057">
              Error: {this.state.error}
            </p>
            <a href="/forgot-password" style={{
            display: 'inline-block',
            padding: '0.75rem 1.5rem',
            backgroundColor: '#2563EB',
            color: 'white',
            borderRadius: '0.5rem',
            textDecoration: 'none',
            fontWeight: '500'
          }} data-id="element-6058">
              Request New Reset Link
            </a>
          </div>
        </div>;
    }
    return this.props.children;
  }
}
const ResetPasswordForm = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: 'Too Short',
    color: 'bg-gray-300'
  });
  const [passwordError, setPasswordError] = useState('');
  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');
  const isValidLink = !!(oobCode && mode === 'resetPassword');
  // DIAGNOSTIC: Prove component mounted
  useEffect(() => {
    console.log('[ResetPasswordPage] Component MOUNTED', {
      hasOobCode: !!oobCode,
      mode,
      isValidLink,
      url: window.location.href
    });
  }, []);
  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(password));
  }, [password]);
  useEffect(() => {
    if (confirmPassword && password !== confirmPassword) {
      setPasswordError('Passwords do not match');
    } else {
      setPasswordError('');
    }
  }, [password, confirmPassword]);
  const calculatePasswordStrength = (pwd: string) => {
    if (!pwd || pwd.length < 1) {
      return {
        score: 0,
        label: 'Too Short',
        color: 'bg-gray-300'
      };
    }
    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 12) score += 1;
    if (/[a-z]/.test(pwd)) score += 0.5;
    if (/[A-Z]/.test(pwd)) score += 0.5;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(pwd)) score += 1;
    score = Math.min(4, score);
    const strengthMap = [{
      score: 0,
      label: 'Too Short',
      color: 'bg-gray-300'
    }, {
      score: 1,
      label: 'Weak',
      color: 'bg-red-500'
    }, {
      score: 2,
      label: 'Fair',
      color: 'bg-orange-500'
    }, {
      score: 3,
      label: 'Good',
      color: 'bg-yellow-500'
    }, {
      score: 4,
      label: 'Strong',
      color: 'bg-green-500'
    }];
    return strengthMap[Math.floor(score)];
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!oobCode) {
      setError('Invalid reset link. Please request a new one.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    try {
      setIsLoading(true);
      console.log('[ResetPasswordPage] Confirming password reset...');
      await confirmPasswordReset(auth, oobCode, password);
      console.log('[ResetPasswordPage] Password reset successful!');
      setIsSuccess(true);
    } catch (err: any) {
      console.error('[ResetPasswordPage] Error:', err);
      if (err.code === 'auth/expired-action-code') {
        setError('This reset link has expired. Please request a new one.');
      } else if (err.code === 'auth/invalid-action-code') {
        setError('This reset link is invalid or has already been used.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use a stronger password.');
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  // Use inline styles as PRIMARY styling to guarantee visibility
  // regardless of Tailwind config or CSS loading issues
  return <div style={{
    minHeight: '100vh',
    background: 'linear-gradient(to right, #0F1626, #1E3A8A, #2C3E50)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem 1rem 3rem 1rem'
  }} data-id="element-6059">
      <div style={{
      maxWidth: '28rem',
      width: '100%',
      backgroundColor: 'white',
      borderRadius: '0.75rem',
      overflow: 'hidden',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
    }} data-id="element-6060">
        <div style={{
        padding: '2.5rem 2rem'
      }} data-id="element-6061">
          {/* Header */}
          <div style={{
          textAlign: 'center',
          marginBottom: '2rem'
        }} data-id="element-6062">
            <div style={{
            margin: '0 auto 1rem auto',
            width: '4rem',
            height: '4rem',
            backgroundColor: '#DBEAFE',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} data-id="element-6063">
              <LockIcon style={{
              width: '2rem',
              height: '2rem',
              color: '#2563EB'
            }} data-id="element-6064" />
            </div>
            <h2 style={{
            fontSize: '1.875rem',
            fontWeight: 'bold',
            color: '#111827',
            marginBottom: '0.5rem'
          }} data-id="element-6065">
              {isSuccess ? 'Password Reset!' : 'Create New Password'}
            </h2>
            <p style={{
            color: '#6B7280'
          }} data-id="element-6066">
              {isSuccess ? 'Your password has been successfully changed.' : isValidLink ? 'Enter your new password below' : 'There was a problem with your reset link'}
            </p>
          </div>

          {/* Error Alert */}
          {error && <div style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: '#FEF2F2',
          borderLeft: '4px solid #EF4444',
          color: '#B91C1C',
          display: 'flex',
          alignItems: 'flex-start',
          borderRadius: '0 0.5rem 0.5rem 0'
        }} data-id="element-6067">
              <AlertCircleIcon style={{
            width: '1.25rem',
            height: '1.25rem',
            marginRight: '0.5rem',
            flexShrink: 0,
            marginTop: '2px'
          }} data-id="element-6068" />
              <div data-id="element-6069">
                <p style={{
              margin: 0
            }} data-id="element-6070">
                  {error}
                </p>
                {(error.includes('expired') || error.includes('invalid') || error.includes('Invalid')) && <a href="/forgot-password" style={{
              color: '#991B1B',
              fontWeight: '500',
              fontSize: '0.875rem',
              marginTop: '0.5rem',
              display: 'inline-block',
              textDecoration: 'underline'
            }} data-id="element-6071">
                    Request a new reset link
                  </a>}
              </div>
            </div>}

          {/* Invalid Link State */}
          {!isValidLink && !isSuccess && <div style={{
          textAlign: 'center'
        }} data-id="element-6072">
              <div style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            backgroundColor: '#FFFBEB',
            borderLeft: '4px solid #F59E0B',
            color: '#92400E',
            display: 'flex',
            alignItems: 'flex-start',
            borderRadius: '0 0.5rem 0.5rem 0',
            textAlign: 'left'
          }} data-id="element-6073">
                <AlertCircleIcon style={{
              width: '1.25rem',
              height: '1.25rem',
              marginRight: '0.5rem',
              flexShrink: 0,
              marginTop: '2px'
            }} data-id="element-6074" />
                <p style={{
              margin: 0
            }} data-id="element-6075">
                  This reset link is invalid or missing required parameters.
                  Please request a new password reset.
                </p>
              </div>
              <a href="/forgot-password" style={{
            display: 'inline-block',
            width: '100%',
            padding: '0.75rem 1rem',
            backgroundColor: '#2563EB',
            color: 'white',
            fontWeight: '500',
            borderRadius: '0.5rem',
            textDecoration: 'none',
            textAlign: 'center',
            boxSizing: 'border-box'
          }} data-id="element-6076">
                Request New Reset Link
              </a>
            </div>}

          {/* Success State */}
          {isSuccess && <div style={{
          textAlign: 'center'
        }} data-id="element-6077">
              <div style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            backgroundColor: '#F0FDF4',
            borderLeft: '4px solid #22C55E',
            color: '#166534',
            display: 'flex',
            alignItems: 'flex-start',
            textAlign: 'left',
            borderRadius: '0 0.5rem 0.5rem 0'
          }} data-id="element-6078">
                <CheckCircleIcon style={{
              width: '1.25rem',
              height: '1.25rem',
              marginRight: '0.5rem',
              flexShrink: 0,
              marginTop: '2px'
            }} data-id="element-6079" />
                <span data-id="element-6080">
                  Your password has been successfully reset. You can now sign in
                  with your new password.
                </span>
              </div>
              <a href="/teacher-login" style={{
            display: 'inline-block',
            width: '100%',
            padding: '0.75rem 1rem',
            backgroundColor: '#2563EB',
            color: 'white',
            fontWeight: '500',
            borderRadius: '0.5rem',
            textDecoration: 'none',
            textAlign: 'center',
            boxSizing: 'border-box'
          }} data-id="element-6081">
                Sign In with New Password
              </a>
            </div>}

          {/* Password Reset Form */}
          {isValidLink && !isSuccess && <form onSubmit={handleSubmit} data-id="element-6082">
              {/* New Password */}
              <div style={{
            marginBottom: '1.5rem'
          }} data-id="element-6083">
                <label htmlFor="reset-password" style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.25rem'
            }} data-id="element-6084">
                  New Password{' '}
                  <span style={{
                color: '#EF4444'
              }} data-id="element-6085">
                    *
                  </span>
                </label>
                <div style={{
              position: 'relative'
            }} data-id="element-6086">
                  <input type={passwordVisible ? 'text' : 'password'} id="reset-password" value={password} onChange={e => setPassword(e.target.value)} style={{
                width: '100%',
                padding: '0.75rem 2.5rem 0.75rem 1rem',
                border: '1px solid #D1D5DB',
                borderRadius: '0.5rem',
                fontSize: '1rem',
                color: '#111827',
                outline: 'none',
                boxSizing: 'border-box'
              }} placeholder="Create a secure password (min. 8 characters)" required disabled={isLoading} data-id="element-6087" />
                  <button type="button" style={{
                position: 'absolute',
                top: '50%',
                right: '0.75rem',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0',
                color: '#6B7280'
              }} onClick={() => setPasswordVisible(!passwordVisible)} disabled={isLoading} data-id="element-6088">
                    {passwordVisible ? <EyeOffIcon style={{
                  width: '1.25rem',
                  height: '1.25rem'
                }} data-id="element-6089" /> : <EyeIcon style={{
                  width: '1.25rem',
                  height: '1.25rem'
                }} data-id="element-6090" />}
                  </button>
                </div>
                {/* Password Strength */}
                <div style={{
              marginTop: '0.5rem'
            }} data-id="element-6091">
                  <div style={{
                width: '100%',
                height: '0.5rem',
                backgroundColor: '#E5E7EB',
                borderRadius: '9999px',
                overflow: 'hidden'
              }} data-id="element-6092">
                    <div style={{
                  height: '100%',
                  width: `${passwordStrength.score / 4 * 100}%`,
                  backgroundColor: passwordStrength.score === 0 ? '#D1D5DB' : passwordStrength.score === 1 ? '#EF4444' : passwordStrength.score === 2 ? '#F97316' : passwordStrength.score === 3 ? '#EAB308' : '#22C55E',
                  transition: 'width 0.3s, background-color 0.3s'
                }} data-id="element-6093" />
                  </div>
                  <div style={{
                marginTop: '0.25rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }} data-id="element-6094">
                    <span style={{
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  color: passwordStrength.score <= 1 ? '#EF4444' : passwordStrength.score === 2 ? '#F97316' : passwordStrength.score === 3 ? '#EAB308' : '#22C55E'
                }} data-id="element-6095">
                      {passwordStrength.label}
                    </span>
                    <span style={{
                  fontSize: '0.75rem',
                  color: '#6B7280'
                }} data-id="element-6096">
                      Use 8+ characters, include a number and symbol
                    </span>
                  </div>
                </div>
              </div>

              {/* Confirm Password */}
              <div style={{
            marginBottom: '1.5rem'
          }} data-id="element-6097">
                <label htmlFor="reset-confirm-password" style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.25rem'
            }} data-id="element-6098">
                  Confirm New Password{' '}
                  <span style={{
                color: '#EF4444'
              }} data-id="element-6099">
                    *
                  </span>
                </label>
                <div style={{
              position: 'relative'
            }} data-id="element-6100">
                  <input type={confirmPasswordVisible ? 'text' : 'password'} id="reset-confirm-password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} style={{
                width: '100%',
                padding: '0.75rem 2.5rem 0.75rem 1rem',
                border: `1px solid ${passwordError ? '#EF4444' : '#D1D5DB'}`,
                borderRadius: '0.5rem',
                fontSize: '1rem',
                color: '#111827',
                outline: 'none',
                boxSizing: 'border-box'
              }} placeholder="Confirm your new password" required disabled={isLoading} data-id="element-6101" />
                  <button type="button" style={{
                position: 'absolute',
                top: '50%',
                right: '0.75rem',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0',
                color: '#6B7280'
              }} onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)} disabled={isLoading} data-id="element-6102">
                    {confirmPasswordVisible ? <EyeOffIcon style={{
                  width: '1.25rem',
                  height: '1.25rem'
                }} data-id="element-6103" /> : <EyeIcon style={{
                  width: '1.25rem',
                  height: '1.25rem'
                }} data-id="element-6104" />}
                  </button>
                </div>
                {passwordError && <p style={{
              marginTop: '0.25rem',
              fontSize: '0.875rem',
              color: '#EF4444'
            }} data-id="element-6105">
                    {passwordError}
                  </p>}
              </div>

              {/* Submit Button */}
              <button type="submit" disabled={isLoading || !!passwordError} style={{
            width: '100%',
            padding: '0.75rem 1rem',
            backgroundColor: isLoading || passwordError ? '#9CA3AF' : '#2563EB',
            color: 'white',
            fontWeight: '500',
            borderRadius: '0.5rem',
            border: 'none',
            fontSize: '1rem',
            cursor: isLoading || passwordError ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s'
          }} data-id="element-6106">
                {isLoading ? <>
                    <svg style={{
                animation: 'spin 1s linear infinite',
                marginRight: '0.75rem',
                width: '1.25rem',
                height: '1.25rem'
              }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-6107">
                      <circle style={{
                  opacity: 0.25
                }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-6108" />
                      <path style={{
                  opacity: 0.75
                }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-6109" />
                    </svg>
                    Resetting Password...
                  </> : 'Reset Password'}
              </button>
            </form>}

          {/* Footer */}
          <div style={{
          marginTop: '2rem',
          textAlign: 'center'
        }} data-id="element-6110">
            <p style={{
            color: '#6B7280'
          }} data-id="element-6111">
              Remember your password?{' '}
              <a href="/teacher-login" style={{
              color: '#2563EB',
              fontWeight: '500',
              textDecoration: 'none'
            }} data-id="element-6112">
                Sign in
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Inline keyframes for spinner */}
      <style data-id="element-6113">{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>;
};
// Exported component with error boundary wrapper
export const ResetPasswordPage = () => {
  return <ResetPasswordErrorBoundary data-id="element-6114">
      <ResetPasswordForm data-id="element-6115" />
    </ResetPasswordErrorBoundary>;
};